import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MapPin, CalendarCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { img } from "../lib/images";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay: 0.05 + i * 0.1, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const site = useSite();
  const h = site.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-32">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7 lg:pt-10">
          <p className="mb-7 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-ink" data-testid="hero-eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-clay" /> {h.eyebrow}
          </p>
          <h1 className="font-display text-[2.6rem] leading-[1.04] text-ink sm:text-6xl lg:text-[4.6rem]" data-testid="hero-title">
            <MaskLine i={0}>{h.title_line1}</MaskLine>
            <MaskLine i={1}>{h.title_line2} <em className="text-clay">{h.title_highlight}</em></MaskLine>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg" data-testid="hero-subtitle">
            {h.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#contact" className="btn-primary group" data-testid="hero-partner-btn">
              Partner With Us <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a href={site.booking_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="hero-check-rates-btn"><CalendarCheck className="h-4 w-4" /> Check Rates</a>
          </div>
        </div>

        <motion.div initial={{ clipPath: "inset(8% 8% 8% 8% round 28px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }} transition={{ duration: 1.1, ease }} className="relative h-[440px] overflow-hidden rounded-[28px] sm:h-[560px] lg:col-span-5 lg:h-[620px]" data-testid="hero-image">
          <motion.img style={{ y: imgY, scale: imgScale }} {...img("hero", "(min-width: 1024px) 42vw, 100vw")} fetchPriority="high" width="1600" height="1067" alt="Villa pool overlooking Bali rice fields" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between rounded-2xl bg-sand/90 px-5 py-4 backdrop-blur-md">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-ink/60">Managed portfolio</p>
              <p className="font-display text-lg text-ink sm:text-xl">Hotels · Villas · Eco-resorts</p>
            </div>
            <MapPin className="h-5 w-5 text-clay" />
          </div>
        </motion.div>
      </div>

      <div className="mx-auto mt-16 max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 border-t border-ink/15 lg:grid-cols-4" data-testid="hero-stats">
          {site.stats.map((s, i) => (
            <div key={i} className={`py-7 pr-4 ${i % 2 ? "pl-5 border-l border-ink/15" : ""} ${i === 2 ? "lg:pl-5 lg:border-l lg:border-ink/15" : ""} ${i > 1 ? "border-t border-ink/15 lg:border-t-0" : ""}`}>
              <p className="font-display text-4xl text-ink sm:text-5xl" data-testid={`stat-value-${i}`}>{s.value}</p>
              <p className="mt-1 text-sm text-ink/60">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
